import { ReactNode, useEffect, useMemo, useRef } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  generatePath,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useParams,
  useSearchParams,
} from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute, NonAuthenticatedRoute } from '@/app/constant';
import { Body } from '@/components/legacy-ui/Body';
import { useGetCollaborators } from '@/hooks/collaborators';
import { Agents } from '@/sections/agents';
import { Assets } from '@/sections/asset';
import { Attacks } from '@/sections/attacks';
import { AuthenticatedApp } from '@/sections/AuthenticatedApp';
import AWSMarketplace from '@/sections/AWSMarketplace';
import AzureMarketplace from '@/sections/AzureMarketplace';
import { CustomerManagement } from '@/sections/customerManagement';
import { PraetorianUserSettings } from '@/sections/customerManagement/settings/user';
import { FeaturebaseSSO } from '@/sections/featurebaseSSO/FeaturebaseSSO';
import Files from '@/sections/Files';
import Hello from '@/sections/Hello';
import { Insights } from '@/sections/insights';
import Integrations from '@/sections/Integrations';
import Jobs from '@/sections/jobs';
import Logout from '@/sections/Logout';
import { Profile } from '@/sections/profile';
import { Seeds } from '@/sections/seeds';
import { Settings } from '@/sections/settings';
import { ForgotPassword, Login, Signup } from '@/sections/signup';
import { LoginSSO } from '@/sections/signup/LoginSSO';
import { SSOAutoRedirect } from '@/sections/signup/SSOAutoRedirect';
import { Techonologies } from '@/sections/technology';
import { ThreatIntelComponent } from '@/sections/threats';
import { Validations } from '@/sections/validations';
import { VulnerabilityRoute } from '@/sections/vulnerabilities';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { encode } from '@/utils/encrypt.util';
import { validateRedirectUrl } from '@/utils/redirect.util';

/**
 * Determines the default route based on user type and tenant access.
 * Praetorian users and multi-tenant customers go to Customer/Tenant Management.
 */
function getDefaultRoute(
  isPraetorianUser: boolean,
  me: string,
  collaboratorEmails: string[]
): AuthenticatedRoute {
  const hasMultiTenantAccess =
    !isPraetorianUser &&
    collaboratorEmails.filter(email => email !== me).length > 1;

  return isPraetorianUser || hasMultiTenantAccess
    ? AuthenticatedRoute.CUSTOMER_MANAGEMENT
    : AuthenticatedRoute.INSIGHTS;
}

function FallbackRoute() {
  const { lastVisitedUserId } = useGlobalState();
  const {
    me,
    whoami,
    isSignedIn,
    logout,
    startImpersonation,
    isPraetorianUser,
    pendingMfaSignIn,
  } = useAuth();

  const defaultUserId = lastVisitedUserId || (isPraetorianUser ? me : '');

  const enablePersonalTenancy =
    whoami?.principal.personal_tenancy_enabled ?? false;

  const { data: collaborators = [], status } = useGetCollaborators({
    doNotImpersonate: true,
  });

  // Derive userId during render using useMemo instead of useState + useEffect
  // This eliminates the setState-in-effect anti-pattern
  const userId = useMemo(() => {
    // 1. If defaultUserId exists (lastVisited or isPraetorianUser), use it
    if (defaultUserId) return defaultUserId;

    // 2. Wait for whoami to load
    if (!whoami) return '';

    // 3. Wait for collaborators to load
    if (status === 'pending') return '';

    // 4. Personal tenancy users, and OIDC/SAML SSO users (whose `me` is
    //    already their main tenant), use their own email.
    //    Google SSO is intentionally excluded — its `me` is the user's
    //    Google email, which for non-Praetorian customers maps to a
    //    disabled personal tenant.
    if (enablePersonalTenancy || whoami.principal.access_type === 'sso') {
      return me;
    }

    // 5. Check tenant access pattern
    const nonOwnTenants = collaborators.filter(c => c.email !== me);

    // 5a. Multi-tenant users (>1 non-own tenant) use own email for customer management
    if (nonOwnTenants.length > 1) return me;

    // 5b. Single tenant or no tenant - use most recent collaborator
    const mostRecentCollaborator = collaborators.slice().sort((a, b) => {
      const dateA = a.updated ? new Date(a.updated).getTime() : 0;
      const dateB = b.updated ? new Date(b.updated).getTime() : 0;
      return dateB - dateA;
    })[0];

    return mostRecentCollaborator?.email ?? '';
  }, [defaultUserId, whoami, enablePersonalTenancy, me, status, collaborators]);

  // Handle the no-collaborators edge case as a side effect
  // This is a valid useEffect pattern since logout() is an async external action
  useEffect(() => {
    if (
      !defaultUserId &&
      whoami &&
      status !== 'pending' &&
      !userId &&
      collaborators.length === 0
    ) {
      toast.error(
        'No collaborators found, please contact support@praetorian.com'
      );
      logout();
    }
  }, [userId, defaultUserId, whoami, status, collaborators.length, logout]);

  const impersonationStarted = useRef(false);

  if (isSignedIn) {
    if (pendingMfaSignIn) {
      toast.error('MFA setup required, please login again');
      logout();
      return null;
    } else if (userId) {
      const defaultRoute = getDefaultRoute(
        isPraetorianUser,
        me,
        collaborators.map(c => c.email)
      );

      // eslint-disable-next-line react-hooks/refs -- intentional: guard to prevent calling startImpersonation more than once during render
      if (userId !== me && !impersonationStarted.current) {
        // Use startImpersonation to properly fetch whoami for the target tenant,
        // ensuring all downstream state (breadcrumb, Statsig, entitlements) is correct.
        impersonationStarted.current = true;
        startImpersonation(userId, defaultRoute, true);
        return null;
      }

      return (
        <Navigate
          to={generatePath(`:userId/${defaultRoute}`, {
            userId: encode(userId),
          })}
          replace
        />
      );
    } else {
      // Return null while determining userId - avoids flash of transition screen
      return null;
    }
  } else {
    return <Navigate to={NonAuthenticatedRoute.LOGIN} replace />;
  }
}

/** Routes that non-Praetorian users without personal tenancy can access on their own tenant. */
const personalTenantAllowedRoutes = new Set([
  AuthenticatedRoute.CUSTOMER_MANAGEMENT,
  AuthenticatedRoute.PROFILE,
]);

function CheckAuth(props: { children: ReactNode }) {
  const { lastVisitedUserId } = useGlobalState();
  const { userId } = useParams();
  const {
    isSignedIn,
    userIdFromUrl,
    me,
    pendingMfaSignIn,
    logout,
    isPraetorianUser,
    whoami,
  } = useAuth();

  const isOwner = userIdFromUrl === me;
  const enablePersonalTenancy =
    whoami?.principal.personal_tenancy_enabled ?? false;

  // Use whoami.accounts as the authoritative list of accessible accounts
  // (includes subtenant-inherited accounts). Falls back to empty while loading.
  const collaboratorEmails = useMemo(
    () => whoami?.accounts ?? [],
    [whoami?.accounts]
  );
  const accountsStatus = whoami ? 'success' : ('pending' as const);

  if (isSignedIn) {
    if (pendingMfaSignIn) {
      toast.error('MFA setup required, please login again');
      logout();
      return null;
    }
    /* mapping for old route */
    if (userId === 'app') {
      const appRoute = location.pathname.replace('/app/', '');

      const routeMap: Record<string, AuthenticatedRoute> = {
        '/app/account': AuthenticatedRoute.SETTINGS,
        '/app/overview': AuthenticatedRoute.INTEGRATIONS,
        '/app/risks': AuthenticatedRoute.VULNERABILITIES,
      };

      return (
        <Navigate
          to={`/${generatePath(
            `:userId/${routeMap[location.pathname] || appRoute}`,
            {
              userId: encode(lastVisitedUserId || me),
            }
          )}${window.location.search}`}
          replace
        />
      );
    }

    if (isOwner) {
      // Non-Praetorian users without personal tenancy can only access
      // a limited set of routes on their own tenant (e.g. tenant management,
      // profile, settings). Redirect everything else to customer management.
      // OIDC/SAML SSO users are exempt: their "own tenant" *is* their main
      // tenant. Google SSO is *not* exempt — for non-Praetorian Google
      // users, "own tenant" is a disabled personal tenant.
      const isOidcSsoUser = whoami?.principal.access_type === 'sso';
      if (!isPraetorianUser && !enablePersonalTenancy && !isOidcSsoUser) {
        const currentRoute = location.pathname.split('/').slice(2).join('/');
        if (
          !personalTenantAllowedRoutes.has(currentRoute as AuthenticatedRoute)
        ) {
          return (
            <Navigate
              to={generatePath(
                `/:userId/${AuthenticatedRoute.CUSTOMER_MANAGEMENT}`,
                {
                  userId: encode(me),
                }
              )}
              replace
            />
          );
        }
      }
      return props.children;
    } else {
      if (accountsStatus !== 'pending') {
        const isUserACollaboratorOfImpersonatedAccount =
          collaboratorEmails.find(email => email === userIdFromUrl);

        if (isUserACollaboratorOfImpersonatedAccount) {
          return props.children;
        } else {
          const defaultRoute = getDefaultRoute(
            isPraetorianUser,
            me,
            collaboratorEmails
          );

          return (
            <Navigate
              to={generatePath(`/:userId/${defaultRoute}`, {
                userId: encode(me),
              })}
              replace
            />
          );
        }
      } else {
        // We are checking if the user is a collaborator, so we don't need to show anything while loading
        return null;
      }
    }
  } else {
    // Encode the current URL (path + search + hash) as a query parameter
    // Include hash in case drawer params were incorrectly parsed as fragment
    // This survives OAuth page reloads and eliminates timing issues
    const fullUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const redirectUrl = encodeURIComponent(fullUrl);
    const loginUrl = `${NonAuthenticatedRoute.LOGIN}?redirect=${redirectUrl}`;
    return <Navigate to={loginUrl} replace />;
  }
}

function CheckNoAuth(props: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();

  if (!isSignedIn) {
    return props.children;
  } else {
    // Read redirect URL from query params (set by CheckAuth)
    // NOTE: Do NOT use decodeURIComponent() - it would decode %23 to #
    // which the browser interprets as fragment delimiter, breaking drawer params
    const rawDestination = searchParams.get('redirect') || '/';

    // SECURITY: Use centralized validation utility to prevent open redirect (CWE-601)
    const destination = validateRedirectUrl(rawDestination);

    return <Navigate to={destination} replace />;
  }
}

function AuthenticatedFallback() {
  const { userId } = useParams();
  const { isPraetorianUser, me, whoami } = useAuth();

  const collaboratorEmails = useMemo(
    () => whoami?.accounts ?? [],
    [whoami?.accounts]
  );
  const defaultRoute = getDefaultRoute(
    isPraetorianUser,
    me,
    collaboratorEmails
  );

  return (
    <Navigate
      to={generatePath(`/:userId/${defaultRoute}`, { userId: userId || '' })}
      replace
    />
  );
}

// Force the Insights subtree to fully remount when the active tenant changes.
// Without this, switching tenants in the impersonation switcher leaks worker
// refs, WebSocket subscriptions, and stale conversation state from tenant A
// into tenant B (ENG-3422 — AI Assistant stuck on "Loading conversation...").
//
// Wrapper rather than inline JSX because AppRoutes captures route elements
// inside useMemo([children]); useAuth() must re-run per render of the matched
// route, not per render of AppRoutes.
//
// Keyed off userIdFromUrl (derived from the routed `:userId` path param) rather
// than auth-derived me/friend. On stopImpersonation, refreshAuth's flushSync
// flips auth state (auth.tsx:184-194) BEFORE navigate updates the URL
// (auth.tsx:250-254). Auth-derived keying would remount Insights at the
// flushSync boundary while :userId still pointed at the impersonated tenant,
// causing one wasted fetch with the stale conversationId. URL-derived keying
// aligns the remount with the actual `:userId` transition so the new mount
// always sees the fresh routed tenant.
//
// Returns null while userIdFromUrl is empty (auth bootstrap window before
// whoami resolves) so the transition `'' → 'user@example.com'` doesn't
// trigger a spurious unmount/remount of the Insights tree.
//
// Exported so __tests__/AppRoute.eng3422.test.tsx can exercise the real
// implementation directly rather than a hand-copied replica.
export function KeyedInsights() {
  const { userIdFromUrl } = useAuth();
  if (!userIdFromUrl) return null;
  return <Insights key={userIdFromUrl} />;
}

export function AppRoutes({ children }: { children: ReactNode }) {
  const router = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <Route element={children}>
            <Route path="" element={<FallbackRoute />} />
            <Route
              path={NonAuthenticatedRoute.MARKETPLACE_AWS}
              element={<AWSMarketplace />}
            />
            <Route
              path={NonAuthenticatedRoute.MARKETPLACE_AZURE}
              element={<AzureMarketplace />}
            />
            <Route
              path={NonAuthenticatedRoute.FEATUREBASE_SSO}
              element={<FeaturebaseSSO />}
            />
            <Route
              path=":userId"
              element={
                <CheckAuth>
                  <AuthenticatedApp>
                    <Body>
                      <Outlet />
                    </Body>
                  </AuthenticatedApp>
                </CheckAuth>
              }
            >
              <Route
                path={AuthenticatedRoute.INSIGHTS}
                element={<KeyedInsights />}
              />
              <Route path={AuthenticatedRoute.SEEDS} element={<Seeds />} />
              <Route path={AuthenticatedRoute.LOGOUT} element={<Logout />} />
              <Route path={AuthenticatedRoute.ASSETS} element={<Assets />} />
              <Route
                path={AuthenticatedRoute.RED_TEAM}
                element={
                  <Navigate
                    to={`${AuthenticatedRoute.ATTACKS}?tab=red-team`}
                    replace
                  />
                }
              />
              <Route path={AuthenticatedRoute.JOBS} element={<Jobs />} />
              <Route
                path={AuthenticatedRoute.SETTINGS}
                element={<Settings />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_SCAN}
                element={<Settings defaultTab="scanSettings" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_NOTIFICATIONS}
                element={<Settings defaultTab="notificationSettings" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_ORGANIZATION}
                element={<Settings defaultTab="orgSettings" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_USER_MANAGEMENT}
                element={<Settings defaultTab="userManagement" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_ACCESS_LOGS}
                element={<Settings defaultTab="accessLogs" />}
              />
              {/* User Profile is a separate page, accessible from AccountDropdown */}
              <Route path={AuthenticatedRoute.PROFILE} element={<Profile />} />
              <Route
                path={AuthenticatedRoute.CUSTOMER_MANAGEMENT_USER_SETTINGS}
                element={<PraetorianUserSettings />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_MONITORING}
                element={<Settings defaultTab="monitoring" />}
              />
              <Route
                path={AuthenticatedRoute.SETTINGS_SUBSCRIPTION}
                element={<Settings defaultTab="subscription" />}
              />
              <Route path={AuthenticatedRoute.FILES} element={<Files />} />
              <Route
                path={AuthenticatedRoute.USERS}
                element={
                  <Navigate
                    to={AuthenticatedRoute.SETTINGS_USER_MANAGEMENT}
                    replace
                  />
                }
              />
              <Route path={AuthenticatedRoute.AGENTS} element={<Agents />} />
              <Route
                path={AuthenticatedRoute.TECHNOLOGIES}
                element={<Techonologies />}
              />
              <Route
                path={AuthenticatedRoute.INTEGRATIONS}
                element={<Integrations />}
              />
              <Route
                path={AuthenticatedRoute.CAPABILITIES}
                element={
                  <Navigate
                    to={`${AuthenticatedRoute.ATTACKS}?tab=capabilities`}
                    replace
                  />
                }
              />
              <Route
                path={AuthenticatedRoute.VULNERABILITIES}
                element={<VulnerabilityRoute />}
              />
              <Route path={AuthenticatedRoute.ATTACKS} element={<Attacks />} />
              <Route
                path={AuthenticatedRoute.VALIDATIONS}
                element={<Validations />}
              />
              {/* Redirect old URL for backward compatibility */}
              <Route
                path="threat-intel"
                element={<Navigate to={AuthenticatedRoute.THREATS} replace />}
              />
              <Route
                path={AuthenticatedRoute.THREATS}
                element={<ThreatIntelComponent />}
              />
              <Route
                path={AuthenticatedRoute.CUSTOMER_MANAGEMENT}
                element={<CustomerManagement />}
              />
              <Route path="" element={<AuthenticatedFallback />} />
              <Route path="*" element={<AuthenticatedFallback />} />
            </Route>
            <Route
              element={
                <CheckNoAuth>
                  <Outlet />
                </CheckNoAuth>
              }
            >
              <Route path={NonAuthenticatedRoute.LOGIN} element={<Login />} />
              <Route
                path={NonAuthenticatedRoute.LOGIN_SSO}
                element={<LoginSSO />}
              />
              <Route path={NonAuthenticatedRoute.SIGNUP} element={<Signup />} />
              <Route
                path={NonAuthenticatedRoute.FORGOT_PASSWORD}
                element={<ForgotPassword />}
              />
              <Route path={NonAuthenticatedRoute.HELLO} element={<Hello />} />
              <Route
                path={NonAuthenticatedRoute.SSO_AUTO}
                element={<SSOAutoRedirect />}
              />
              <Route
                path={NonAuthenticatedRoute.GOODBYE}
                element={<Navigate to={NonAuthenticatedRoute.LOGIN} replace />}
              />
              <Route path="*" element={<FallbackRoute />} />
            </Route>
          </Route>
        )
      ),
    [children]
  );

  return <RouterProvider router={router} />;
}
